import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Slide } from "react-toastify";
import {
  AlertCircle,
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  ArrowUpToLine,
  Bot,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Copy,
  Download,
  Eye,
  EyeOff,
  Film,
  Languages,
  Loader2,
  Maximize,
  Minimize,
  MoreHorizontal,
  Pause,
  Pencil,
  PencilLine,
  Play,
  RotateCcw,
  Save,
  Scissors,
  Search,
  Trash2,
  X,
} from "lucide-react";

import api, { fetchAllPages, unwrapList } from "utils/api";
import { API_ENDPOINTS } from "config/api";
import { extractErrorMessage } from "utils/apiError";
import { downloadFileFromUrl } from "utils/download";
import { playbackFractionFromPointer } from "utils/playback";
import { toast } from "utils/toast";
import { detectTextLang, isRtlLanguage, translations as i18n } from "utils/i18n";
import { useLanguage } from "context/languageContext";
import {
  applySuggestionWords,
  buildTranslatedLinesPayload,
  buildTranscriptionLinesPayload,
  deleteWord,
  EXPORT_FORMATS,
  formatDuration,
  getLanguageNames,
  groupSuggestionsByLine,
  languageLabel,
  latestReviewPending,
  lineText,
  linesToPlainText,
  mergeLines,
  resegmentByWordCount,
  setWordText,
  splitLine,
  textPreview,
  transcriptName,
  TRANSLATION_LANGUAGES,
  withSourceIndex,
} from "utils/transcript";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { SidebarTrigger } from "components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { NameDialog } from "components/name-dialog";
import { ExportDialog } from "components/export-dialog";
import { VideoExportDialog } from "components/video-export-dialog";
import {
  captionBackgroundStyle,
  captionTextStyle,
  CaptionStylePanel,
  DEFAULT_CAPTION_STYLE,
  resolveExportFont,
} from "components/caption-style-panel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";

const IN_FLIGHT_STATUSES = ["pending", "processing"];
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// Translation and AI review are both "enqueue, then poll every 3s until the
// backend says completed/failed" — with no ceiling, a job the backend loses
// (crashed worker, wedged queue) polls forever and the button spins forever
// with nothing on screen to say why. This is the ceiling: give up locally,
// tell the user, and let them retry — a client-side backstop, not a claim
// about what actually went wrong server-side.
const JOB_TIMEOUT_MS = 10 * 60 * 1000;

// There's no "list reviews for this transcript" endpoint, so a transcript
// that was reviewed and found perfectly clean (zero suggestions — a valid,
// successful outcome) leaves no row anywhere for a later page load to find.
// This is the one bit of review history that genuinely can't be recovered
// from the API, so it's the one bit kept locally instead.
const REVIEWED_KEY_PREFIX = "arkii_reviewed_";

function wasReviewed(transcriptId) {
  return localStorage.getItem(REVIEWED_KEY_PREFIX + transcriptId) === "1";
}

function markReviewed(transcriptId) {
  localStorage.setItem(REVIEWED_KEY_PREFIX + transcriptId, "1");
}

// Translation's "survives a refresh" trick is a boolean the backend itself
// puts on the transcript (`is_translating`), so there's nothing for the
// frontend to lose track of. Review has no such flag and no "list reviews
// for this transcript" endpoint either — the only place a review's id lives
// is wherever the POST response landed — so this is the substitute: the one
// in-flight review's id, stashed the moment it's started and cleared the
// moment it settles, purely so a reload can resume polling the same job
// instead of forgetting it (and letting the button look idle while it's
// still running, and still billed).
const INFLIGHT_REVIEW_KEY_PREFIX = "arkii_review_inflight_";

function getInFlightReviewId(transcriptId) {
  return localStorage.getItem(INFLIGHT_REVIEW_KEY_PREFIX + transcriptId);
}

function setInFlightReviewId(transcriptId, reviewId) {
  localStorage.setItem(INFLIGHT_REVIEW_KEY_PREFIX + transcriptId, reviewId);
}

function clearInFlightReviewId(transcriptId) {
  localStorage.removeItem(INFLIGHT_REVIEW_KEY_PREFIX + transcriptId);
}

// Lucide has no numbered seek icons, so these carry the jump size on the
// face of the arrow the way every player does. Ported from arkii-product's
// seek-icons.tsx.
function SeekBack() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3 8.5" />
      <path d="M3 4v4.5h4.5" />
      <text x="12" y="15.6" textAnchor="middle" stroke="none" fill="currentColor" fontSize="9" fontWeight="600">5</text>
    </svg>
  );
}

function SeekForward() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 12a8.5 8.5 0 1 1-2.6-6.1L21 8.5" />
      <path d="M21 4v4.5h-4.5" />
      <text x="12" y="15.6" textAnchor="middle" stroke="none" fill="currentColor" fontSize="9" fontWeight="600">5</text>
    </svg>
  );
}

// Lucide has CheckCheck (a doubled check) but no companion for "reject all" —
// this is a filled "remove from list" glyph rather than lucide's usual
// stroke style, so it doesn't take stroke/fill props the way the others do.
function RejectAllIcon(props) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" {...props}>
      <path
        fill="currentColor"
        d="M17.5 5a.5.5 0 0 1 0 1h-15a.5.5 0 0 1 0-1zm0 3a.5.5 0 0 1 0 1h-15a.5.5 0 0 1 0-1zm-7.243 3a5.5 5.5 0 0 0-.657 1H2.5a.5.5 0 0 1 0-1zM9 14.5q0 .252.022.5H2.5a.5.5 0 0 1 0-1h6.522a6 6 0 0 0-.022.5m5.5 4.5a4.5 4.5 0 1 0 0-9a4.5 4.5 0 0 0 0 9m1.146-2.646L14.5 15.207l-1.146 1.147a.5.5 0 0 1-.708-.708l1.147-1.146l-1.147-1.146a.5.5 0 0 1 .708-.708l1.146 1.147l1.146-1.147a.5.5 0 0 1 .708.708L15.207 14.5l1.147 1.146a.5.5 0 0 1-.708.708"
      />
    </svg>
  );
}

// Custom icon for the resize button — not in lucide, so hand-drawn to match
// its stroke conventions (currentColor, 2px round-cap/join strokes).
function WordsPerLineIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 12H18M6 12L8 9M6 12L8 15M18 12L16 9M18 12L16 15M21 21V3M3 21V3"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// One icon-only action with a hover/aria label — the line toolbar's hover
// buttons, and (via `spinning`) the Translation dropdown's per-language
// actions. Deliberately tiny and muted so it reads as a quiet affordance
// rather than a row of primary actions.
function LineActionButton({ icon: Icon, label, active, disabled, spinning, onClick }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-slot="button"
          disabled={disabled}
          aria-label={label}
          aria-pressed={active}
          onClick={onClick}
          className={cn(
            "flex size-6 shrink-0 appearance-none items-center justify-center rounded border-0 bg-transparent text-muted-foreground outline-none transition-colors",
            "hover:bg-accent hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:pointer-events-none disabled:opacity-40",
            active && "bg-accent text-foreground"
          )}
        >
          <Icon className={cn("size-3.5", spinning && "animate-spin")} aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

export default function TranscriptEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = i18n[language].editor;
  // Computed once per render and reused everywhere LANGUAGE_NAMES used to be
  // read directly — see the fix note on the old static export in
  // utils/transcript.js. `language` is in the poll effect's own dependency
  // array below so a mid-flight UI language switch doesn't leave a stale
  // closure over an old set of names.
  const languageNames = getLanguageNames(language);

  const [transcript, setTranscript] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [lines, setLines] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingWord, setEditingWord] = useState(null);
  const cancelEditRef = useRef(false);

  // Translation words use the same inline editing gesture as source words,
  // but live in a separately persisted document with its own line indexes.
  const [editingTranslationWord, setEditingTranslationWord] = useState(null);
  const translationCancelEditRef = useRef(false);

  // Which line's hover toolbar is "armed" for a click-a-word action — either
  // { lineIndex, action: "edit" } or { lineIndex, action: "split" }. Merge
  // has no selection step, so it never sets this.
  const [armedAction, setArmedAction] = useState(null);
  const [translationArmedAction, setTranslationArmedAction] = useState(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(2);
  const [scrubbing, setScrubbing] = useState(false);
  const audioRef = useRef(null);
  const audioRetriedRef = useRef(false);
  // The actual <video>/<audio> src — deliberately NOT read straight from
  // transcript.audio_url. That field comes back on every GET (including the
  // 3s poll below, which keeps running well after playback has started
  // whenever a translation/review is also in flight) and can carry a freshly
  // re-signed URL each time even though it points at the exact same file;
  // binding src to it directly meant the poll tick restarted playback every
  // 3 seconds. This is set once when a transcript first loads, and
  // otherwise only by handleAudioError's explicit "the current URL actually
  // failed" retry — never by routine polling.
  const [mediaUrl, setMediaUrl] = useState(null);

  // Live-preview-only, like speedIndex above — never sent to the backend,
  // and (also like speedIndex) deliberately not reset by the id-change
  // effect below, so it survives navigating between caption transcripts as
  // a sticky per-session preference instead of resetting on every load.
  const [captionStyle, setCaptionStyle] = useState(DEFAULT_CAPTION_STYLE);
  // Ordered independently from the transcript workspace's translation
  // visibility: this list controls only the text stacked over the video.
  // The first entry renders on top and is therefore the visual priority.
  const [captionLanguages, setCaptionLanguages] = useState([]);
  const [captionTranslationRows, setCaptionTranslationRows] = useState({});
  const [loadingCaptionLanguages, setLoadingCaptionLanguages] = useState(() => new Set());

  // Caps the video's height at exactly "whatever's left in the column after
  // the style panel" — measured, not guessed, so video+panel always fit the
  // viewport with no scrolling, however tall the panel gets (it grows: a
  // preset row, a slider, more controls than the first pass had) and however
  // short the browser window is. Must be declared before the early returns
  // below (Rules of Hooks), so it reads transcript/lines directly rather
  // than the later `completed`/`isCaption` consts derived from them.
  const videoColumnRef = useRef(null);
  const captionPanelWrapRef = useRef(null);
  // Wraps both the <video> and the caption overlay together — fullscreen is
  // requested on THIS element, not the bare video, specifically so the
  // caption (a sibling of <video>, not a descendant) goes fullscreen along
  // with it instead of being left behind.
  const videoStageRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === videoStageRef.current);
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  function handleToggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      videoStageRef.current?.requestFullscreen().catch(() => toast.error(tr.toasts.cantEnterFullscreen));
    }
  }

  const [videoMaxHeight, setVideoMaxHeight] = useState(null);
  // The video's *actual* rendered height, independent of the cap above (a
  // narrow-aspect-ratio video can render shorter than videoMaxHeight allows)
  // — used only to scale the caption font-size for the export API's "size is
  // px at a 1080-tall reference frame" contract. The preview has no fixed
  // frame height of its own (it's however tall the column happens to be), so
  // captionStyle.size is authored in that same 1080-reference unit and
  // scaled down/up to whatever the preview is actually rendering at, rather
  // than converting it at export time — one formula, used both places.
  const [videoRenderedHeight, setVideoRenderedHeight] = useState(null);
  const hasLines = !!lines;
  useLayoutEffect(() => {
    const showsCaptionVideo = transcript?.source === "caption" && transcript?.status === "completed" && hasLines;
    if (!showsCaptionVideo) return;
    const column = videoColumnRef.current;
    const panel = captionPanelWrapRef.current;
    const video = audioRef.current;
    if (!column || !panel) return;

    function recompute() {
      const columnStyle = getComputedStyle(column);
      const verticalPadding = parseFloat(columnStyle.paddingTop || "0") + parseFloat(columnStyle.paddingBottom || "0");
      const available = column.clientHeight - verticalPadding - panel.offsetHeight;
      // 12px slack matches the panel's own border-t/pt-3 gap, so the video's
      // bottom edge and the panel's top edge read as evenly spaced.
      setVideoMaxHeight(Math.max(60, Math.floor(available - 12)));
      if (video) setVideoRenderedHeight(video.getBoundingClientRect().height || null);
    }

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(column);
    observer.observe(panel);
    if (video) observer.observe(video);
    return () => observer.disconnect();
  }, [transcript?.source, transcript?.status, hasLines]);

  // 1080-reference px -> actual preview px. Falls back to the max-height cap
  // (a close estimate) before the true render height has been measured, and
  // to 1 (no scaling) if neither is available yet — always renders *something*
  // reasonable rather than waiting on a measurement to show the caption at all.
  const captionPreviewScale = (videoRenderedHeight ?? videoMaxHeight ?? 1080) / 1080;

  // Burned-in video export: same enqueue-then-poll shape as translation/
  // review above, no credit charge (per the API). `videoExportJob` is the
  // latest polled row, null when nothing's running or the last one was
  // cleared (completed/failed/timed out) — there's no "list exports for
  // this transcript" recovery the way review has via localStorage, so an
  // export in flight is lost on reload the same way a translation is.
  const [videoExportJob, setVideoExportJob] = useState(null);
  const [videoExportStarting, setVideoExportStarting] = useState(false);
  const videoExportStartedAtRef = useRef(null);
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const [videoDownloadProgress, setVideoDownloadProgress] = useState(null);
  const [videoExportDialogOpen, setVideoExportDialogOpen] = useState(false);

  const [jump, setJump] = useState(null);
  const scrollRef = useRef(null);
  const activeWordRef = useRef(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const searchPanelRef = useRef(null);
  const currentMatchRef = useRef(null);

  const resizeInputId = useId();
  const [resizeOpen, setResizeOpen] = useState(false);
  const [resizeConfirmOpen, setResizeConfirmOpen] = useState(false);
  // Whether the input has actually changed the grouping since the panel
  // opened — false right after opening (the transcript still shows its
  // saved grouping untouched). Gates the Confirm button, the Save-button
  // guard, and the translation-preview suppression below, without needing a
  // separate "default vs. custom" mode.
  const [hasPendingResize, setHasPendingResize] = useState(false);
  const [wordsPerLine, setWordsPerLine] = useState(6);
  // The input's own displayed text — kept separate from `wordsPerLine` (the
  // clamped 1-12 value actually applied to the transcript) so the field can
  // freely pass through an empty string or an out-of-range value while the
  // user is mid-edit, instead of a controlled value fighting every
  // keystroke. Reconciled back to `wordsPerLine` on blur.
  const [wordsPerLineInput, setWordsPerLineInput] = useState("6");
  const resizePanelRef = useRef(null);
  // Snapshot of `lines` taken when the resize panel opens — every edit
  // re-derives from this (never from the already-resegmented `lines`), so
  // repeated adjustments are reproducible and reverting is a single
  // assignment. Null whenever the panel is closed.
  const snapshotLinesRef = useRef(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creditConfirmation, setCreditConfirmation] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState(EXPORT_FORMATS[0]);

  // Translation is async, same shape as transcription itself: POST enqueues
  // a translation row (pending -> processing -> completed/failed) and this
  // page polls GET /translation/<id>/ until it settles. `translationJobs` is
  // the latest polled row per language, keyed by language code — more than
  // one target language could be in flight at once, so this is a map rather
  // than a single in-flight flag. `translations` caches a completed job's
  // `translated_lines` per language, populated either by finishing a job
  // started here or by recovering a past one via GET /translation/ (see
  // handleViewClick) — either way this in-memory cache is what's lost on
  // reload, not the underlying data. `visibleLangs` is the set of languages
  // currently appended under the transcript, not just a single one.
  const [translations, setTranslations] = useState({});
  const [visibleLangs, setVisibleLangs] = useState(() => new Set());
  const [translationMenuOpen, setTranslationMenuOpen] = useState(false);
  const [translationJobs, setTranslationJobs] = useState({});
  const [dirtyTranslations, setDirtyTranslations] = useState(() => new Set());
  const [savingTranslations, setSavingTranslations] = useState(() => new Set());
  // When this page first noticed each language's job in flight — not when
  // the job itself was created, since an adopted-from-elsewhere job (see
  // handleTranslateClick) has no earlier timestamp available here either
  // way. Keyed by language, pruned as jobs leave the in-flight set.
  const translationStartedAtRef = useRef({});
  // Languages currently mid-click on Translate/Retranslate, from the moment
  // it's pressed until either an in-progress job is adopted or a fresh one
  // is POSTed — translationJobs isn't populated for the (async) lookup in
  // between, so relying on it alone to disable the button leaves a real gap
  // a fast click can slip through and fire a duplicate, billed job.
  const [startingLangs, setStartingLangs] = useState(() => new Set());

  const [project, setProject] = useState(null);

  // AI review: `reviewJob` is the latest polled Review record (job/status
  // only, no content) while a run is in flight; `suggestions` holds every
  // *pending* ReviewSuggestion belonging to the *latest* review run only —
  // "regenerate" makes the newest run authoritative, so an older run's
  // leftovers stop being shown or actionable the moment a newer one lands
  // (see latestReviewPending). `hasBeenReviewed` is stickier than either of
  // those: true once this transcript has ever had a review complete, even
  // after every suggestion from it has been resolved (or there were none to
  // begin with) — it's what the AI Review dropdown checks to decide between
  // offering a first review vs. view/hide + regenerate. `reviewVisible` is
  // the view/hide toggle for the diff overlay, independent of whether there's
  // anything to show. `resolvingIds` covers the moment a suggestion's accept/
  // reject PATCH is in flight, so its buttons disable rather than allow a
  // second, overlapping resolve.
  const [reviewJob, setReviewJob] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [hasBeenReviewed, setHasBeenReviewed] = useState(false);
  const [reviewVisible, setReviewVisible] = useState(true);
  const [reviewStarting, setReviewStarting] = useState(false);
  const [resolvingIds, setResolvingIds] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  // Any AI job owns a snapshot of this transcript while it runs. Keep both
  // editable documents read-only until every translation/review settles so
  // user PATCHes cannot race those background results.
  const anyTranslationInFlight =
    startingLangs.size > 0 ||
    Object.values(translationJobs).some((job) => IN_FLIGHT_STATUSES.includes(job.status)) ||
    !!transcript?.is_translating;
  const reviewInFlight = reviewStarting || (!!reviewJob && IN_FLIGHT_STATUSES.includes(reviewJob.status));
  const editsLocked = anyTranslationInFlight || reviewInFlight;
  const hasUnsavedChanges = dirty || dirtyTranslations.size > 0;
  const isAnythingSaving = saving || savingTranslations.size > 0;

  const duration = transcript?.duration_seconds ?? 0;
  const creditSeconds = Number(transcript?.billed_seconds ?? duration);
  const creditMinutes = Number.isFinite(creditSeconds) ? Math.max(0, creditSeconds) / 60 : 0;
  const creditMinutesLabel = creditMinutes.toLocaleString(language === "ar" ? "ar-IQ" : "en-GB", {
    maximumFractionDigits: 2,
  });
  const creditCost = tr.dialogs.creditMinutes(creditMinutesLabel, creditMinutes);

  // Initial fetch. Also the poll target while the transcript is still being
  // produced — this page doubles as "fetch one transcript before showing the
  // segment editor."
  useEffect(() => {
    let cancelled = false;
    setTranscript(null);
    setLoadError(null);
    setLines(null);
    setDirty(false);
    setMediaUrl(null);
    setCaptionLanguages([]);
    setCaptionTranslationRows({});
    setLoadingCaptionLanguages(new Set());
    audioRetriedRef.current = false;
    setVideoExportJob(null);
    setVideoExportStarting(false);
    setDownloadingVideo(false);
    setTranslations({});
    setVisibleLangs(new Set());
    setTranslationJobs({});
    setDirtyTranslations(new Set());
    setSavingTranslations(new Set());
    setEditingTranslationWord(null);
    setTranslationArmedAction(null);
    setStartingLangs(new Set());
    setReviewJob(null);
    setSuggestions([]);
    setHasBeenReviewed(false);
    setReviewVisible(true);
    setReviewStarting(false);
    setResolvingIds(new Set());
    setBulkConfirm(null);
    setBulkRunning(false);

    api
      .get(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id))
      .then(({ data }) => {
        if (cancelled) return;
        setTranscript(data);
        setMediaUrl(data.audio_url);
        if (data.status === "completed") setLines(withSourceIndex(data.transcription_lines ?? []));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(extractErrorMessage(err));
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const captionSourceLanguage = transcript?.language_detected || transcript?.language_requested;

  useEffect(() => {
    if (transcript?.source !== "caption" || transcript.status !== "completed" || !captionSourceLanguage) return;
    setCaptionLanguages((current) => (current.length > 0 ? current : [captionSourceLanguage]));
  }, [id, transcript?.source, transcript?.status, captionSourceLanguage]);

  // The transcript detail only tells us that a language has translation
  // history, not whether its newest usable row is completed. Resolve that
  // once for caption videos so the language picker is enabled only by real,
  // selectable translations and has the detail id ready on first selection.
  useEffect(() => {
    if (transcript?.source !== "caption" || transcript.status !== "completed") return;
    let cancelled = false;
    fetchAllPages(API_ENDPOINTS.TRANSLATION.LIST, { params: { transcript_id: id } })
      .then((rows) => {
        if (cancelled) return;
        const latestCompleted = {};
        rows.forEach((row) => {
          if (row.status !== "completed" || row.target_language === captionSourceLanguage) return;
          const previous = latestCompleted[row.target_language];
          if (!previous || row.created_at > previous.created_at) latestCompleted[row.target_language] = row;
        });
        setCaptionTranslationRows(latestCompleted);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, transcript?.source, transcript?.status, captionSourceLanguage]);

  // Transcriptions only carry a project *id* — fetch the project separately
  // so its name can be shown (and linked to) beneath the transcript name.
  useEffect(() => {
    if (!transcript?.project) {
      setProject(null);
      return;
    }
    let cancelled = false;
    api
      .get(API_ENDPOINTS.PROJECT.DETAIL(transcript.project))
      .then(({ data }) => {
        if (!cancelled) setProject(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [transcript?.project]);

  const pollRef = useRef(null);
  useEffect(() => {
    // Also keeps polling while `is_translating` is set, even once the
    // transcript's own (transcription) status is "completed" — otherwise a
    // translation kicked off from elsewhere (another tab, a prior visit)
    // would never get its flag refreshed back to false on this page, and
    // the Translate button below would spin forever.
    const inFlight = transcript && (IN_FLIGHT_STATUSES.includes(transcript.status) || transcript.is_translating);
    if (inFlight && !pollRef.current) {
      pollRef.current = setInterval(() => {
        api
          .get(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id))
          .then(({ data }) => {
            setTranscript(data);
            if (data.status === "completed") setLines(withSourceIndex(data.transcription_lines ?? []));
          })
          .catch(() => {});
      }, 3000);
    }
    if (!inFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [transcript, id]);

  // Polls every language with a job still pending/processing, one shared
  // timer rather than one interval per language. Re-running this effect on
  // every `translationJobs` update (rather than a ref-tracked interval, like
  // the transcript poll above) means each tick's in-flight list is always
  // current — a language finishing removes it, a new one starting adds it —
  // and the effect naturally stops scheduling once none are left.
  useEffect(() => {
    // Recomputed from `language` (a stable primitive already in this
    // effect's own deps) rather than closing over the outer `tr`/
    // `languageNames` consts directly — those are recreated every render
    // (getLanguageNames especially, a fresh object literal each call), which
    // would otherwise force this effect (and its setInterval) to tear down
    // and restart on every unrelated render instead of only on a real
    // language switch.
    const localTr = i18n[language].editor;
    const localLanguageNames = getLanguageNames(language);

    const inFlightLangs = Object.entries(translationJobs)
      .filter(([, job]) => IN_FLIGHT_STATUSES.includes(job.status))
      .map(([lang]) => lang);

    // Start (or keep) each in-flight language's clock; drop the clock for
    // anything no longer in flight so a later, unrelated translation to the
    // same language starts fresh instead of inheriting a stale timestamp.
    inFlightLangs.forEach((lang) => {
      translationStartedAtRef.current[lang] ??= Date.now();
    });
    Object.keys(translationStartedAtRef.current).forEach((lang) => {
      if (!inFlightLangs.includes(lang)) delete translationStartedAtRef.current[lang];
    });

    if (inFlightLangs.length === 0) return;

    const interval = setInterval(() => {
      inFlightLangs.forEach((lang) => {
        const startedAt = translationStartedAtRef.current[lang];
        if (startedAt && Date.now() - startedAt > JOB_TIMEOUT_MS) {
          delete translationStartedAtRef.current[lang];
          setTranslationJobs((prev) => {
            const next = { ...prev };
            delete next[lang];
            return next;
          });
          toast.error(localTr.toasts.translateTimeout(localLanguageNames[lang] ?? lang.toUpperCase()));
          return;
        }
        api
          .get(API_ENDPOINTS.TRANSLATION.DETAIL(translationJobs[lang].id))
          .then(({ data }) => {
            setTranslationJobs((prev) => (prev[lang]?.id === data.id ? { ...prev, [lang]: data } : prev));
            if (data.status === "completed") {
              setTranslations((prev) => ({ ...prev, [lang]: withSourceIndex(data.translated_lines ?? []) }));
              setCaptionTranslationRows((prev) => ({ ...prev, [lang]: data }));
              setTranscript((prev) =>
                prev
                  ? { ...prev, translated_languages: Array.from(new Set([...(prev.translated_languages ?? []), lang])) }
                  : prev
              );
              toast.success(localTr.toasts.translated(localLanguageNames[lang] ?? lang.toUpperCase()));
            } else if (data.status === "failed") {
              toast.error(data.failure_reason || localTr.toasts.translateFailed(localLanguageNames[lang] ?? lang.toUpperCase()));
            }
          })
          .catch(() => {});
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [translationJobs, language]);

  // Mount/reload recovery: finds whichever review is newest across every run
  // this transcript has ever had, and adopts only that one's still-pending
  // suggestions (see latestReviewPending) — an older run's unresolved rows
  // exist server-side too, but the product model is "last review wins," so
  // they're intentionally left out. `hasBeenReviewed` only ever flips true
  // here (from either signal), never back to false, since finding nothing
  // this time doesn't erase that a review happened at some point.
  const loadSuggestionsForTranscript = useCallback(async () => {
    try {
      const rows = await fetchAllPages(API_ENDPOINTS.REVIEW.SUGGESTIONS, { params: { transcript_id: id } });
      setSuggestions(latestReviewPending(rows));
      if (rows.length > 0 || wasReviewed(id)) setHasBeenReviewed(true);
    } catch {
      if (wasReviewed(id)) setHasBeenReviewed(true);
    }
  }, [id]);

  useEffect(() => {
    if (transcript?.status !== "completed") return;
    // A review started before a reload has no id anywhere in memory to
    // resume polling — except this transcript's own stashed one (see
    // INFLIGHT_REVIEW_KEY_PREFIX above). Seeding a "pending" placeholder is
    // enough to get the poll effect below going; whatever its real status
    // turns out to be replaces this within one tick.
    const inFlightId = getInFlightReviewId(id);
    if (inFlightId) setReviewJob({ id: inFlightId, status: "pending" });
    loadSuggestionsForTranscript();
  }, [transcript?.status, id, loadSuggestionsForTranscript]);

  // Called once a review this page itself started reaches "completed" —
  // scoped to that one review's id (not transcript_id) so a regenerate's
  // result never gets mixed back in with whatever an older run left pending.
  const loadSuggestionsForReview = useCallback(async (reviewId, announce) => {
    try {
      const rows = await fetchAllPages(API_ENDPOINTS.REVIEW.SUGGESTIONS, { params: { review_id: reviewId } });
      const pending = rows.filter((row) => row.status === "pending");
      setSuggestions(pending);
      setReviewVisible(true);
      if (announce) {
        toast.success(pending.length ? tr.toasts.reviewFoundCount(pending.length) : tr.toasts.reviewFoundNothing);
      }
    } catch (err) {
      if (announce) toast.error(extractErrorMessage(err));
    }
  }, [tr]);

  const reviewPollRef = useRef(null);
  // Same reasoning as translationStartedAtRef above: when THIS page first
  // saw the current reviewJob in flight, not when the review itself was
  // created — a review resumed from the localStorage id after a reload has
  // no earlier timestamp available here either way.
  const reviewStartedAtRef = useRef(null);
  useEffect(() => {
    const inFlight = reviewJob && IN_FLIGHT_STATUSES.includes(reviewJob.status);
    if (inFlight) {
      reviewStartedAtRef.current ??= Date.now();
    } else {
      reviewStartedAtRef.current = null;
    }
    if (inFlight && !reviewPollRef.current) {
      reviewPollRef.current = setInterval(() => {
        if (reviewStartedAtRef.current && Date.now() - reviewStartedAtRef.current > JOB_TIMEOUT_MS) {
          reviewStartedAtRef.current = null;
          clearInFlightReviewId(id);
          setReviewJob(null);
          toast.error(tr.toasts.reviewTimeout);
          return;
        }
        api
          .get(API_ENDPOINTS.REVIEW.DETAIL(reviewJob.id))
          .then(({ data }) => {
            setReviewJob(data);
            if (data.status === "completed") {
              setHasBeenReviewed(true);
              markReviewed(id);
              clearInFlightReviewId(id);
              loadSuggestionsForReview(data.id, true);
            } else if (data.status === "failed") {
              clearInFlightReviewId(id);
              toast.error(data.failure_reason || tr.toasts.reviewFailed);
            }
          })
          .catch(() => {});
      }, 3000);
    }
    if (!inFlight && reviewPollRef.current) {
      clearInterval(reviewPollRef.current);
      reviewPollRef.current = null;
    }
    return () => {
      if (reviewPollRef.current) {
        clearInterval(reviewPollRef.current);
        reviewPollRef.current = null;
      }
    };
  }, [reviewJob, id, loadSuggestionsForReview, tr]);

  // Same shape as the review poll above, including the timeout backstop —
  // an encode is somewhat more likely to genuinely take a while than a
  // translation/review, but JOB_TIMEOUT_MS is generous (10 minutes) and a
  // render that's really still running just gets a fresh poll cycle on the
  // next Export click, same recovery path as everything else here.
  const videoExportPollRef = useRef(null);
  useEffect(() => {
    const inFlight = videoExportJob && IN_FLIGHT_STATUSES.includes(videoExportJob.status);
    if (inFlight) {
      videoExportStartedAtRef.current ??= Date.now();
    } else {
      videoExportStartedAtRef.current = null;
    }
    if (inFlight && !videoExportPollRef.current) {
      videoExportPollRef.current = setInterval(() => {
        if (videoExportStartedAtRef.current && Date.now() - videoExportStartedAtRef.current > JOB_TIMEOUT_MS) {
          videoExportStartedAtRef.current = null;
          setVideoExportJob(null);
          toast.error(tr.toasts.videoExportTimeout);
          return;
        }
        api
          .get(API_ENDPOINTS.EXPORT.VIDEO_DETAIL(videoExportJob.id))
          .then(({ data }) => {
            setVideoExportJob((prev) => (prev?.id === data.id ? data : prev));
            if (data.status === "completed") {
              toast.success(tr.toasts.videoExportReady);
            } else if (data.status === "failed") {
              toast.error(data.failure_reason || tr.toasts.videoExportFailed);
            }
          })
          .catch(() => {});
      }, 3000);
    }
    if (!inFlight && videoExportPollRef.current) {
      clearInterval(videoExportPollRef.current);
      videoExportPollRef.current = null;
    }
    return () => {
      if (videoExportPollRef.current) {
        clearInterval(videoExportPollRef.current);
        videoExportPollRef.current = null;
      }
    };
  }, [videoExportJob, tr]);

  // Real playback fires "timeupdate" only a handful of times a second, which
  // is too coarse to keep the active-word highlight in step — so also poll
  // the element every frame while it's actually playing.
  useEffect(() => {
    if (!playing) return;
    let frame;
    const step = () => {
      if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[speedIndex];
  }, [speedIndex]);

  useEffect(() => {
    function handler(event) {
      if (!dirty && dirtyTranslations.size === 0) return;
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, dirtyTranslations]);

  // A job can also begin in another tab and be discovered by polling. Cancel
  // any half-finished inline gesture, and revert an open resize preview, as
  // soon as the shared editor lock turns on.
  useLayoutEffect(() => {
    if (!editsLocked) {
      cancelEditRef.current = false;
      translationCancelEditRef.current = false;
      return;
    }
    if (editingWord) cancelEditRef.current = true;
    if (editingTranslationWord) translationCancelEditRef.current = true;
    if (document.activeElement?.isContentEditable) document.activeElement.blur();
    setEditingWord(null);
    setEditingTranslationWord(null);
    setArmedAction(null);
    setTranslationArmedAction(null);
    setBulkConfirm(null);
    if (resizeOpen) {
      if (snapshotLinesRef.current) setLines(snapshotLinesRef.current);
      snapshotLinesRef.current = null;
      setResizeOpen(false);
      setHasPendingResize(false);
      setResizeConfirmOpen(false);
    }
  }, [editsLocked, editingWord, editingTranslationWord, resizeOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    function handlePointer(event) {
      if (searchPanelRef.current && !searchPanelRef.current.contains(event.target)) setSearchOpen(false);
    }
    function handleKey(event) {
      if (event.key === "Escape") setSearchOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [searchOpen]);

  useEffect(() => {
    if (!armedAction) return;
    function handlePointer(event) {
      if (!event.target.closest(`[data-line-index="${armedAction.lineIndex}"]`)) setArmedAction(null);
    }
    function handleKey(event) {
      if (event.key === "Escape") setArmedAction(null);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [armedAction]);

  useEffect(() => {
    if (!translationArmedAction) return;
    function handlePointer(event) {
      const { lang, lineIndex } = translationArmedAction;
      if (!event.target.closest(`[data-translation-line="${lang}:${lineIndex}"]`)) setTranslationArmedAction(null);
    }
    function handleKey(event) {
      if (event.key === "Escape") setTranslationArmedAction(null);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [translationArmedAction]);

  const updateJump = useCallback(() => {
    const word = activeWordRef.current;
    const container = scrollRef.current;
    if (!word || !container) {
      setJump(null);
      return;
    }
    const bounds = container.getBoundingClientRect();
    const rect = word.getBoundingClientRect();
    if (rect.bottom < bounds.top + 24) setJump("up");
    else if (rect.top > bounds.bottom - 24) setJump("down");
    else setJump(null);
  }, []);

  useEffect(updateJump, [currentTime, updateJump]);

  const needle = query.trim().toLowerCase();

  // Flat, document-order list of every word containing the needle, so
  // "next/previous match" can walk it exactly like a browser's find bar.
  const matches = useMemo(() => {
    if (!needle || !lines) return [];
    const result = [];
    lines.forEach((line, lineIndex) => {
      line.words.forEach((word, wordPos) => {
        if (word.word.trim().toLowerCase().includes(needle)) result.push({ lineIndex, wordPos });
      });
    });
    return result;
  }, [lines, needle]);

  // lineIndex -> (wordPos -> suggestion), re-derived from live `lines` on
  // every change so a suggestion keeps finding its word across split/merge/
  // resegment — see groupSuggestionsByLine.
  const suggestionsByLine = useMemo(() => groupSuggestionsByLine(lines ?? [], suggestions), [lines, suggestions]);

  useEffect(() => {
    setMatchIndex(0);
  }, [needle]);

  useEffect(() => {
    if (matches.length === 0) return;
    currentMatchRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [matchIndex, matches]);

  function goToMatch(delta) {
    if (matches.length === 0) return;
    setMatchIndex((prev) => (prev + delta + matches.length) % matches.length);
  }

  function seekToTime(time) {
    const clamped = Math.max(0, Math.min(duration, time));
    setCurrentTime(clamped);
    if (audioRef.current) audioRef.current.currentTime = clamped;
  }

  function seekBy(delta) {
    seekToTime(currentTime + delta);
  }

  function seekFromPointer(clientX, track) {
    const bounds = track.getBoundingClientRect();
    const ratio = playbackFractionFromPointer(clientX, bounds, isRtlLanguage(language));
    seekToTime(ratio * duration);
  }

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.pause();
    else {
      const label = transcript?.source === "caption" ? tr.player.mediaVideo : tr.player.mediaAudio;
      audio.play().catch(() => toast.error(tr.toasts.cantPlayMedia(label)));
    }
  }

  async function handleAudioError() {
    const label = transcript?.source === "caption" ? tr.player.mediaVideo : tr.player.mediaAudio;
    if (audioRetriedRef.current) {
      toast.error(tr.toasts.cantLoadMedia(label));
      return;
    }
    audioRetriedRef.current = true;
    try {
      const { data } = await api.get(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id));
      setTranscript((prev) => (prev ? { ...prev, audio_url: data.audio_url } : prev));
      setMediaUrl(data.audio_url);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  // Clearing a word's text entirely and committing (Enter or blur) deletes
  // it — there's no separate delete affordance, this just reuses the same
  // edit gesture. The backend has no explicit delete op either: a word is
  // dropped by leaving it out of the PATCH payload (see deleteWord).
  function commitWordEdit(lineIndex, wordPos, rawText) {
    if (editsLocked) return;
    const trimmed = rawText.trim();
    const original = lines[lineIndex].words[wordPos].word.trim();
    if (trimmed === original) return;
    if (!trimmed) {
      setLines((prev) => deleteWord(prev, lineIndex, wordPos));
    } else {
      setLines((prev) => setWordText(prev, lineIndex, wordPos, trimmed));
    }
    setDirty(true);
  }

  // Arms a line's "edit a word" or "split" toolbar button — the next click
  // on a word in that line performs the action (see the word span's onClick
  // below). Clicking an already-armed button disarms it.
  function toggleArmed(lineIndex, action) {
    if (editsLocked) return;
    setArmedAction((prev) => (prev?.lineIndex === lineIndex && prev.action === action ? null : { lineIndex, action }));
  }

  function handleMerge(lineIndex, direction) {
    if (editsLocked) return;
    setEditingWord(null);
    setLines((prev) => mergeLines(prev, lineIndex, direction));
    setDirty(true);
  }

  function markTranslationDirty(lang) {
    setDirtyTranslations((prev) => new Set(prev).add(lang));
  }

  function updateTranslationLines(lang, updater) {
    if (editsLocked) return;
    setTranslations((prev) => ({ ...prev, [lang]: updater(prev[lang] ?? []) }));
    markTranslationDirty(lang);
  }

  function commitTranslationWordEdit(lang, lineIndex, wordPos, rawText) {
    const currentLines = translations[lang];
    const original = currentLines?.[lineIndex]?.words?.[wordPos]?.word.trim();
    if (original == null) return;
    const trimmed = rawText.trim();
    if (trimmed === original) return;
    updateTranslationLines(lang, (current) =>
      trimmed ? setWordText(current, lineIndex, wordPos, trimmed) : deleteWord(current, lineIndex, wordPos)
    );
  }

  function toggleTranslationArmed(lang, lineIndex, action) {
    if (editsLocked) return;
    setTranslationArmedAction((prev) =>
      prev?.lang === lang && prev.lineIndex === lineIndex && prev.action === action
        ? null
        : { lang, lineIndex, action }
    );
  }

  function handleTranslationSplit(lang, lineIndex, wordPos) {
    if (editsLocked) return;
    setEditingTranslationWord(null);
    setTranslationArmedAction(null);
    updateTranslationLines(lang, (current) => splitLine(current, lineIndex, wordPos));
  }

  function handleTranslationMerge(lang, lineIndex, direction) {
    if (editsLocked) return;
    setEditingTranslationWord(null);
    setTranslationArmedAction(null);
    updateTranslationLines(lang, (current) => mergeLines(current, lineIndex, direction, { anchorToTarget: true }));
  }

  async function handleSave() {
    if (!lines || editsLocked || saving || savingTranslations.size > 0) return;

    const saveSource = dirty;
    const translationLangs = Array.from(dirtyTranslations).filter(
      (lang) => translationJobs[lang]?.id && translations[lang]
    );
    if (!saveSource && translationLangs.length === 0) return;

    setSaving(saveSource);
    setSavingTranslations(new Set(translationLangs));

    const failed = [];
    if (saveSource) {
      try {
        const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id), {
          transcription_lines: buildTranscriptionLinesPayload(lines),
        });
        // Source indices are tied to the transcript's immutable ASR words,
        // so keep the locally cached identities rather than reindexing the
        // normalized response.
        setTranscript(data);
        setDirty(false);
      } catch (err) {
        failed.push(err);
      }
    }

    // Save translations only after the source PATCH settles. A source-only
    // split/merge can realign completed translations on the backend, so
    // sending both writes concurrently would let them race over the same
    // Translation row.
    const translationResults = await Promise.allSettled(
      translationLangs.map((lang) =>
        api.patch(API_ENDPOINTS.TRANSLATION.DETAIL(translationJobs[lang].id), {
          translated_lines: buildTranslatedLinesPayload(translations[lang]),
        })
      )
    );
    translationResults.forEach((result, index) => {
      const lang = translationLangs[index];
      if (result.status === "rejected") {
        failed.push(result.reason);
        return;
      }

      const data = result.value.data;
      // Translation indices are relative to its current saved word list.
      // Adopt and reindex the normalized response after every successful
      // save, especially after deletions have closed gaps in that list.
      setTranslationJobs((prev) => ({ ...prev, [lang]: data }));
      setCaptionTranslationRows((prev) => ({ ...prev, [lang]: data }));
      setTranslations((prev) => ({
        ...prev,
        [lang]: withSourceIndex(data.translated_lines ?? []),
      }));
      setDirtyTranslations((prev) => {
        const next = new Set(prev);
        next.delete(lang);
        return next;
      });
    });

    setSaving(false);
    setSavingTranslations(new Set());
    if (failed.length === 0) toast.success(tr.toasts.changesSaved);
    else toast.error(failed.length === 1 ? extractErrorMessage(failed[0]) : tr.toasts.changesFailedCount(failed.length));
  }

  function openResizePanel() {
    snapshotLinesRef.current = lines;
    const totalWords = lines.reduce((sum, line) => sum + line.words.length, 0);
    const avg = lines.length ? totalWords / lines.length : 6;
    const initial = Math.max(1, Math.min(12, Math.round(avg)));
    setWordsPerLine(initial);
    setWordsPerLineInput(String(initial));
    setHasPendingResize(false);
    setResizeOpen(true);
  }

  // armedAction/editingWord/search matches are all keyed by line position —
  // stale the instant resegmentation changes line boundaries (same
  // precondition handleMerge above already enforces for editingWord alone).
  function resetLineKeyedState() {
    setArmedAction(null);
    setEditingWord(null);
    setTranslationArmedAction(null);
    setEditingTranslationWord(null);
    setSearchOpen(false);
    setQuery("");
  }

  function applyResegmentation(n) {
    if (editsLocked) return;
    const clamped = Math.max(1, Math.min(12, Math.round(n)));
    setWordsPerLine(clamped);
    setWordsPerLineInput(String(clamped));
    setLines(resegmentByWordCount(snapshotLinesRef.current, clamped));
    setHasPendingResize(true);
    resetLineKeyedState();
  }

  function handleWordsInputChange(event) {
    const raw = event.target.value;
    setWordsPerLineInput(raw);
    if (raw === "") return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    applyResegmentation(parsed);
  }

  // Reconciles a field left empty (or mid-edit) back to the last real value
  // once the user's done with it, so it never lingers blank/inconsistent.
  function handleWordsInputBlur() {
    setWordsPerLineInput(String(wordsPerLine));
  }

  // useCallback (this file's only other case is updateJump, for the same
  // reason): referenced from the outside-click/Escape effect below, and a
  // plain function would get a fresh identity every render, forcing that
  // effect to re-subscribe its document listeners on every render instead
  // of only when resizeOpen/saving actually change. No reactive deps of its
  // own — it only touches a ref and stable setters — so an empty dep array
  // is correct, not a suppressed warning.
  const closeResizePanel = useCallback((revert) => {
    if (revert) {
      setLines(snapshotLinesRef.current);
      resetLineKeyedState();
    }
    snapshotLinesRef.current = null;
    setResizeOpen(false);
    setHasPendingResize(false);
  }, []);

  function toggleResizePanel() {
    if (editsLocked) return;
    if (resizeOpen) {
      if (saving) return;
      closeResizePanel(true);
    } else {
      if (dirtyTranslations.size > 0) {
        toast.error(tr.toasts.saveTranslationsBeforeResize);
        return;
      }
      openResizePanel();
    }
  }

  // After a resize-confirm PATCH, every language this page has any cached
  // translation for might now be misaligned against the new line count —
  // the backend realigns translation rows itself when segments change, so
  // this just re-fetches each one's latest completed row instead of
  // trusting the pre-resize cache. Same two calls as handleViewClick's
  // recovery path above.
  async function refetchTranslations() {
    const langs = new Set([...visibleLangs, ...Object.keys(translations)]);
    if (langs.size === 0) return;
    await Promise.all(
      Array.from(langs).map(async (lang) => {
        try {
          const { data } = await api.get(API_ENDPOINTS.TRANSLATION.LIST, { params: { transcript_id: id } });
          const done = unwrapList(data).find((row) => row.target_language === lang && row.status === "completed");
          if (!done) return;
          const { data: full } = await api.get(API_ENDPOINTS.TRANSLATION.DETAIL(done.id));
          setTranslationJobs((prev) => ({ ...prev, [lang]: full }));
          setCaptionTranslationRows((prev) => ({ ...prev, [lang]: full }));
          setTranslations((prev) => ({ ...prev, [lang]: withSourceIndex(full.translated_lines ?? []) }));
        } catch {
          // Best-effort — the resize PATCH itself already succeeded; a
          // stale cached translation just won't self-heal until next
          // reload/toggle if this particular lookup fails.
        }
      })
    );
  }

  async function handleConfirmResize() {
    if (!lines || editsLocked || saving || !hasPendingResize) return;
    setSaving(true);
    try {
      const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id), {
        transcription_lines: buildTranscriptionLinesPayload(lines),
      });
      setTranscript(data);
      setDirty(false);
      setHasPendingResize(false);
      toast.success(tr.toasts.segmentationUpdated);
      await refetchTranslations();
      closeResizePanel(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Ignored while resizeConfirmOpen: the confirmation AlertDialog portals
  // outside resizePanelRef, so without this guard, clicking its Cancel/
  // Confirm buttons (or pressing Escape to dismiss it) would register as an
  // "outside click" on the panel underneath and revert the pending preview
  // out from under the modal that's asking whether to keep it.
  useEffect(() => {
    if (!resizeOpen) return;
    function handlePointer(event) {
      if (saving || resizeConfirmOpen) return;
      if (resizePanelRef.current && !resizePanelRef.current.contains(event.target)) closeResizePanel(true);
    }
    function handleKey(event) {
      if (saving || resizeConfirmOpen) return;
      if (event.key === "Escape") closeResizePanel(true);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [resizeOpen, saving, resizeConfirmOpen, closeResizePanel]);

  async function handleRename(name) {
    try {
      const { data } = await api.patch(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id), { name });
      setTranscript((prev) => (prev ? { ...prev, name: data.name ?? name } : prev));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleDelete() {
    try {
      await api.delete(API_ENDPOINTS.TRANSCRIPTION.DETAIL(id));
      navigate("/transcribe");
      toast.success(tr.toasts.transcriptionDeleted);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function copyTranscript() {
    try {
      await navigator.clipboard.writeText(linesToPlainText(lines));
      toast.success(tr.toasts.transcriptCopied);
    } catch {
      toast.error(tr.toasts.clipboardUnavailable);
    }
  }

  // Enqueues a translation — credit is deducted immediately, but the row
  // comes back "pending" with no text yet. The polling effect above picks it
  // up as soon as it lands in translationJobs and carries it to completion.
  async function runTranslate(lang) {
    try {
      const { data } = await api.post(API_ENDPOINTS.TRANSLATION.TRANSLATE, {
        transcript_id: id,
        target_language: lang,
      });
      setTranslationJobs((prev) => ({ ...prev, [lang]: data.translation }));
      // The app's other toasts use react-toastify's default Bounce transition;
      // this one is deliberately calmer (Slide), scoped to just this call —
      // starting a multi-minute background job doesn't need the same
      // attention-grabbing entrance as an instant success/error.
      toast.success(tr.toasts.translatingTo(languageNames[lang] ?? lang.toUpperCase()), { transition: Slide });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  // Translate/Retranslate: always ends in a fresh job (that's the point of
  // "retranslate" — redo it, not recover the old one), except a job for this
  // language already running is adopted instead of doubling up — whether
  // this page started it, or another tab/session did and this page just
  // hasn't heard about it yet. Turns the language's line on so it appears
  // once the job lands.
  async function handleTranslateClick(lang) {
    if (startingLangs.has(lang)) return;
    if (translationJobs[lang] && IN_FLIGHT_STATUSES.includes(translationJobs[lang].status)) return;
    if (hasUnsavedChanges) {
      toast.error(tr.toasts.saveBeforeTranslate);
      return;
    }

    setStartingLangs((prev) => new Set(prev).add(lang));
    setVisibleLangs((prev) => new Set(prev).add(lang));

    let inProgress = null;
    try {
      const { data } = await api.get(API_ENDPOINTS.TRANSLATION.LIST, { params: { transcript_id: id } });
      inProgress = unwrapList(data).find(
        (row) => row.target_language === lang && IN_FLIGHT_STATUSES.includes(row.status)
      );
    } catch {
      // Lookup failed — fall through and create a fresh translation anyway
      // rather than leaving the click stuck with no result at all.
    }

    if (inProgress) {
      setTranslationJobs((prev) => ({ ...prev, [lang]: inProgress }));
    } else {
      await runTranslate(lang);
    }

    setStartingLangs((prev) => {
      const next = new Set(prev);
      next.delete(lang);
      return next;
    });
  }

  function requestTranslate(lang) {
    if (startingLangs.has(lang)) return;
    if (translationJobs[lang] && IN_FLIGHT_STATUSES.includes(translationJobs[lang].status)) return;
    if (hasUnsavedChanges) {
      toast.error(tr.toasts.saveBeforeTranslate);
      return;
    }
    setTranslationMenuOpen(false);
    setCreditConfirmation({ type: "translation", lang });
  }

  // View: toggles a language's line on/off in the transcript — the "on/off"
  // side of what used to also decide whether to translate. The first time
  // it's turned on for a language this page has no content for yet, it
  // recovers the newest completed translation via GET /translation/ instead
  // of creating a new one — this button never bills. (Only enabled once a
  // language has something to show; see the "View" button's `disabled`.)
  async function handleViewClick(lang) {
    const wasVisible = visibleLangs.has(lang);
    setVisibleLangs((prev) => {
      const next = new Set(prev);
      if (wasVisible) next.delete(lang);
      else next.add(lang);
      return next;
    });
    if (wasVisible || translations[lang]) return;
    try {
      const { data } = await api.get(API_ENDPOINTS.TRANSLATION.LIST, { params: { transcript_id: id } });
      const done = unwrapList(data).find((row) => row.target_language === lang && row.status === "completed");
      if (!done) return;
      const { data: full } = await api.get(API_ENDPOINTS.TRANSLATION.DETAIL(done.id));
      setTranslationJobs((prev) => ({ ...prev, [lang]: full }));
      setCaptionTranslationRows((prev) => ({ ...prev, [lang]: full }));
      setTranslations((prev) => ({ ...prev, [lang]: withSourceIndex(full.translated_lines ?? []) }));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  function handleCaptionLanguagesChange(nextLanguages) {
    const next = nextLanguages.length > 0 ? nextLanguages : [captionSourceLanguage];
    setCaptionLanguages(next);

    next.forEach((lang) => {
      if (
        lang === captionSourceLanguage ||
        translations[lang] ||
        loadingCaptionLanguages.has(lang)
      ) {
        return;
      }
      const row = captionTranslationRows[lang] || translationJobs[lang];
      if (!row?.id || row.status !== "completed") return;

      setLoadingCaptionLanguages((prev) => new Set(prev).add(lang));
      api
        .get(API_ENDPOINTS.TRANSLATION.DETAIL(row.id))
        .then(({ data }) => {
          setTranslationJobs((prev) => ({ ...prev, [lang]: data }));
          setCaptionTranslationRows((prev) => ({ ...prev, [lang]: data }));
          setTranslations((prev) => ({ ...prev, [lang]: withSourceIndex(data.translated_lines ?? []) }));
        })
        .catch((err) => {
          setCaptionLanguages((current) => current.filter((code) => code !== lang));
          toast.error(extractErrorMessage(err));
        })
        .finally(() => {
          setLoadingCaptionLanguages((prev) => {
            const remaining = new Set(prev);
            remaining.delete(lang);
            return remaining;
          });
        });
    });
  }

  // Enqueues an AI review — credit (1:1 with billed_seconds) is deducted
  // immediately, same as translation. Returns right away with a "pending"
  // job; the poll effect above carries it to completion and loads whatever
  // suggestions it produced.
  async function handleStartReview() {
    if (reviewStarting || (reviewJob && IN_FLIGHT_STATUSES.includes(reviewJob.status))) return;
    if (hasUnsavedChanges) {
      toast.error(tr.toasts.saveBeforeReview);
      return;
    }
    setReviewStarting(true);
    try {
      const { data } = await api.post(API_ENDPOINTS.REVIEW.TRIGGER, { transcript_id: id });
      setReviewJob(data.review);
      setInFlightReviewId(id, data.review.id);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setReviewStarting(false);
    }
  }

  function requestReview() {
    if (reviewStarting || (reviewJob && IN_FLIGHT_STATUSES.includes(reviewJob.status))) return;
    if (hasUnsavedChanges) {
      toast.error(tr.toasts.saveBeforeReview);
      return;
    }
    setCreditConfirmation({ type: "review" });
  }

  function handleCreditConfirm() {
    const action = creditConfirmation;
    setCreditConfirmation(null);
    if (action?.type === "translation") {
      handleTranslateClick(action.lang);
    } else if (action?.type === "review") {
      handleStartReview();
    }
  }

  // Enqueues a burned-in video export with the style currently shown in the
  // preview. Unlike translate/review, a fresh click while one is already
  // completed just starts another render (e.g. after tweaking the style) —
  // there's no "retranslate"-style confirm needed since this never bills.
  async function handleExportVideo() {
    if (videoExportStarting || (videoExportJob && IN_FLIGHT_STATUSES.includes(videoExportJob.status))) return;
    setVideoExportStarting(true);
    try {
      const { data } = await api.post(API_ENDPOINTS.EXPORT.VIDEO, {
        transcript_id: id,
        // Preserve the same language selection and top-to-bottom priority
        // the user is looking at in the preview. The export service resolves
        // these codes to completed caption tracks when it enqueues the job.
        languages: effectiveCaptionLanguages,
        // Font is remapped since the API's enum is a subset of what the
        // picker offers (see resolveExportFont). Background color and opacity
        // are sent verbatim so the rendered box matches the live preview.
        style: {
          font: resolveExportFont(captionStyle.font),
          size: captionStyle.size,
          bold: captionStyle.bold,
          italic: captionStyle.italic,
          positionY: captionStyle.positionY,
          color: captionStyle.color,
          backgroundMode: captionStyle.backgroundMode,
          backgroundColor: captionStyle.backgroundColor,
          backgroundOpacity: captionStyle.backgroundOpacity,
        },
      });
      setVideoExportJob(data.export);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      // A failure here means the job was never created — there's nothing
      // for the dialog's spinner to be waiting on, so leaving it open would
      // just spin forever instead of reflecting the toast that already
      // explains what happened.
      setVideoExportDialogOpen(false);
    } finally {
      setVideoExportStarting(false);
    }
  }

  // Opens the export modal from the header's Export dropdown and kicks off
  // a fresh render — handleExportVideo's own guard no-ops this if one's
  // already in flight, so reopening the dropdown mid-export just resumes
  // watching it rather than double-firing.
  function handleOpenVideoExport() {
    setVideoExportDialogOpen(true);
    handleExportVideo();
  }

  async function handleDownloadVideo() {
    const url = videoExportJob?.video_url;
    if (downloadingVideo || !url) return;
    setDownloadingVideo(true);
    setVideoDownloadProgress(0);
    try {
      await downloadFileFromUrl(url, `${transcriptName(transcript, language)}.mp4`, setVideoDownloadProgress);
    } finally {
      setDownloadingVideo(false);
      setVideoDownloadProgress(null);
    }
  }

  // Core of accept/reject, shared by a single line's buttons and "accept/
  // reject all" — `targets` is an explicit [{ lineIndex, wordPos, suggestion
  // }] list so both callers can resolve however many suggestions they own in
  // one batch. Each suggestion is independently resolvable on the backend, so
  // this uses allSettled and applies/prunes only whichever ones actually
  // succeeded rather than making the whole batch all-or-nothing.
  async function resolveSuggestions(targets, status) {
    if (targets.length === 0 || editsLocked) return 0;
    const ids = targets.map((t) => t.suggestion.id);
    setResolvingIds((prev) => new Set([...prev, ...ids]));
    const results = await Promise.allSettled(
      targets.map((t) => api.patch(API_ENDPOINTS.REVIEW.SUGGESTION_DETAIL(t.suggestion.id), { status }).then(() => t))
    );
    const succeeded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    if (status === "accepted" && succeeded.length) {
      setLines((prev) =>
        applySuggestionWords(
          prev,
          succeeded.map((t) => ({ lineIndex: t.lineIndex, wordPos: t.wordPos, correctedWord: t.suggestion.corrected_word }))
        )
      );
    }
    const succeededIds = new Set(succeeded.map((t) => t.suggestion.id));
    setSuggestions((prev) => prev.filter((s) => !succeededIds.has(s.id)));
    const failedCount = targets.length - succeeded.length;
    if (failedCount > 0) {
      const statusWord = status === "accepted" ? tr.toasts.statusAccepted : tr.toasts.statusRejected;
      toast.error(tr.toasts.correctionsFailed(failedCount, statusWord));
    }
    setResolvingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((i) => next.delete(i));
      return next;
    });
    return succeeded.length;
  }

  function handleResolveLine(lineIndex, status) {
    if (editsLocked) return;
    const wordMap = suggestionsByLine.get(lineIndex);
    if (!wordMap) return;
    resolveSuggestions(
      Array.from(wordMap.entries()).map(([wordPos, suggestion]) => ({ lineIndex, wordPos, suggestion })),
      status
    );
  }

  async function handleResolveAll(status) {
    if (editsLocked) return;
    const targets = [];
    suggestionsByLine.forEach((wordMap, lineIndex) => {
      wordMap.forEach((suggestion, wordPos) => targets.push({ lineIndex, wordPos, suggestion }));
    });
    setBulkRunning(true);
    const succeededCount = await resolveSuggestions(targets, status);
    setBulkRunning(false);
    setBulkConfirm(null);
    if (succeededCount === targets.length) {
      toast.success(status === "accepted" ? tr.toasts.allAccepted : tr.toasts.allRejected);
    }
  }

  if (!transcript && !loadError) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-3">
          <SidebarTrigger className="shrink-0 text-muted-foreground" />
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate("/transcribe")}
            className="shrink-0 text-muted-foreground"
          >
            <ChevronLeft className="rtl:rotate-180" />
            <span className="sr-only">{tr.toolbar.back}</span>
          </Button>
          <Skeleton className="h-4 w-48" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-6 py-10">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="grid gap-1 pb-7 md:grid-cols-[5rem_minmax(0,1fr)] md:gap-4">
                <Skeleton className="h-3 w-10 md:mt-1" />
                <div className="grid gap-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-11/12" />
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-3">
          <SidebarTrigger className="shrink-0 text-muted-foreground" />
        </header>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-muted-foreground">{loadError}</p>
          <Button variant="outline" onClick={() => navigate("/transcribe")}>
            {tr.toolbar.backToTranscriptions}
          </Button>
        </div>
      </div>
    );
  }

  const completed = transcript.status === "completed" && lines;
  const languageCode = transcript.language_detected || transcript.language_requested;
  const rtl = isRtlLanguage(languageCode);
  // Translating a transcript into its own language isn't a real option.
  const translateTargets = TRANSLATION_LANGUAGES.filter((lang) => lang !== languageCode);

  // Only a caption-sourced transcript has a video worth previewing —
  // everything else only ever had audio. Each selected language resolves its
  // own independently segmented line at currentTime; ordering this array is
  // what makes one language appear above another in the video overlay.
  const isCaption = transcript.source === "caption";
  const videoWidth = Number(transcript.video_metadata?.width);
  const videoHeight = Number(transcript.video_metadata?.height);
  const videoFormatLabel = transcript.video_metadata?.format
    ? String(transcript.video_metadata.format).replace(/^\./, "").toUpperCase()
    : tr.toolbar.exportVideo;
  const videoResolutionLabel = videoWidth > 0 && videoHeight > 0 ? `${videoWidth} × ${videoHeight}` : null;
  const fullTranscriptName = transcriptName(transcript, language);
  const previewTranscriptName = textPreview(fullTranscriptName, 29);
  const captionTranslationCodes = TRANSLATION_LANGUAGES.filter(
    (lang) =>
      lang !== languageCode &&
      (captionTranslationRows[lang]?.status === "completed" ||
        translationJobs[lang]?.status === "completed" ||
        !!translations[lang])
  );
  const effectiveCaptionLanguages = captionLanguages.length > 0 ? captionLanguages : [languageCode];
  const captionLanguageOptions = [languageCode, ...captionTranslationCodes].map((code) => ({
    code,
    label: languageNames[code] ?? code.toUpperCase(),
  }));
  const captionBlocks = isCaption && completed
    ? effectiveCaptionLanguages.flatMap((lang) => {
        const languageLines = lang === languageCode ? lines : translations[lang] ?? [];
        const activeLine = languageLines.find((line) => currentTime >= line.start && currentTime < line.end);
        const text = activeLine ? lineText(activeLine) : "";
        return text ? [{ lang, text }] : [];
      })
    : [];

  // Shared by whichever single media element is actually mounted below —
  // the visible <video> for a caption transcript, or the hidden <audio>
  // otherwise. Never both at once: two elements pointed at the same
  // audioRef would leave one of them loading the same file unreferenced.
  const mediaHandlers = {
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onEnded: () => setPlaying(false),
    onTimeUpdate: (event) => setCurrentTime(event.currentTarget.currentTime),
    onError: handleAudioError,
  };

  // While a pending resize preview is live, `translations` is still indexed
  // against the pre-resize line count/boundaries — showing it here would
  // pair old translated text with the wrong new line. Suppressed until
  // refetchTranslations() replaces the cache after Confirm (or the preview
  // is reverted, which restores index-alignment automatically).
  const segmentationPreviewActive = resizeOpen && hasPendingResize;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <header className="transcript-editor-header relative flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2 sm:flex-nowrap sm:py-3">
        <SidebarTrigger className="shrink-0 text-muted-foreground" />

        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => navigate("/transcribe")}
          className="shrink-0 text-muted-foreground"
        >
          <ChevronLeft className="rtl:rotate-180" />
          <span className="sr-only">{tr.toolbar.back}</span>
        </Button>

        <h1 className="min-w-0 flex-1 basis-0 leading-tight">
          <span
            lang={detectTextLang(fullTranscriptName)}
            className="block truncate text-sm font-semibold leading-tight"
            aria-label={fullTranscriptName}
            title={previewTranscriptName === fullTranscriptName ? undefined : fullTranscriptName}
          >
            {previewTranscriptName}
          </span>
          <span className="flex items-center gap-1 pt-0.5 text-xs leading-tight font-normal text-muted-foreground">
            <span>{formatDuration(transcript.duration_seconds)}</span>
            <span aria-hidden="true">&middot;</span>
            <span>{languageLabel(transcript)}</span>
            {project ? (
              <>
                <span aria-hidden="true">&middot;</span>
                <Link
                  to={`/projects/${project.id}`}
                  lang={detectTextLang(project.name)}
                  className="truncate text-muted-foreground no-underline hover:text-foreground"
                >
                  {project.name}
                </Link>
              </>
            ) : null}
          </span>
        </h1>

        {completed ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" className="shrink-0 text-muted-foreground">
                <MoreHorizontal />
                <span className="sr-only">{tr.toolbar.fileActions}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => setRenameOpen(true)}>
                <PencilLine />
                {tr.toolbar.rename}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 />
                {tr.toolbar.delete}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        <div className="transcript-editor-actions ms-auto flex basis-full flex-wrap items-center justify-end gap-1.5 sm:basis-auto sm:gap-2">
          {completed ? (
            <>
              <span className="flex items-center gap-2 pe-1 text-xs text-muted-foreground">
                <span
                  className={cn("size-2 rounded-full", isAnythingSaving || hasUnsavedChanges ? "bg-[var(--pending)]" : "bg-emerald-500")}
                  aria-hidden="true"
                />
                {editsLocked
                  ? tr.toolbar.statusLocked
                  : isAnythingSaving
                    ? tr.toolbar.statusSaving
                    : hasUnsavedChanges
                      ? tr.toolbar.statusUnsaved
                      : tr.toolbar.statusSaved}
              </span>

              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleSave}
                disabled={editsLocked || !hasUnsavedChanges || isAnythingSaving || (resizeOpen && hasPendingResize)}
                className="text-muted-foreground"
              >
                {isAnythingSaving ? <Loader2 className="animate-spin" /> : <Save />}
                <span className="sr-only">{tr.toolbar.saveChanges}</span>
              </Button>

              <div className="relative" ref={resizePanelRef}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={toggleResizePanel}
                  disabled={editsLocked}
                  className="text-muted-foreground"
                >
                  <WordsPerLineIcon />
                  <span className="sr-only">{tr.toolbar.resizeLines}</span>
                </Button>

                {resizeOpen ? (
                  <div className="absolute end-0 top-full z-20 mt-2 w-64 max-w-[calc(100vw-1.5rem)] rounded-md border border-border bg-popover p-3 shadow-md">
                    <div className="grid gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor={resizeInputId}>{tr.toolbar.wordsPerLine}</Label>
                        <Input
                          id={resizeInputId}
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={12}
                          value={wordsPerLineInput}
                          disabled={saving || editsLocked}
                          onChange={handleWordsInputChange}
                          onBlur={handleWordsInputBlur}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") event.currentTarget.blur();
                          }}
                          className="h-8 w-16 text-center"
                        />
                      </div>

                      <div className="border-t border-border pt-3">
                        <Button
                          size="sm"
                          disabled={saving || editsLocked || !hasPendingResize}
                          onClick={() => setResizeConfirmOpen(true)}
                          className="w-full"
                        >
                          {saving ? <Loader2 className="animate-spin" /> : null}
                          {tr.dialogs.confirm}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="relative" ref={searchPanelRef}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setSearchOpen((open) => !open)}
                  className="text-muted-foreground"
                >
                  <Search />
                  <span className="sr-only">{tr.toolbar.search}</span>
                </Button>

                {searchOpen ? (
                  <div className="absolute end-0 top-full z-20 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-md border border-border bg-popover p-3 shadow-md">
                    <div className="grid gap-2">
                      <div className="flex items-center gap-1">
                        <Input
                          autoFocus
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              goToMatch(event.shiftKey ? -1 : 1);
                            }
                          }}
                          placeholder={tr.toolbar.findPlaceholder}
                          aria-label={tr.toolbar.findPlaceholder}
                          className="h-8"
                        />
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          {needle ? `${matches.length ? matchIndex + 1 : 0}/${matches.length}` : ""}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={matches.length === 0}
                          onClick={() => goToMatch(-1)}
                          className="shrink-0 text-muted-foreground"
                        >
                          <ChevronUp />
                          <span className="sr-only">{tr.toolbar.previousMatch}</span>
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          disabled={matches.length === 0}
                          onClick={() => goToMatch(1)}
                          className="shrink-0 text-muted-foreground"
                        >
                          <ChevronDown />
                          <span className="sr-only">{tr.toolbar.nextMatch}</span>
                        </Button>
                      </div>
                      {!needle ? (
                        <p className="text-xs text-muted-foreground">{tr.toolbar.matchesHint}</p>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <Button variant="ghost" size="icon-sm" onClick={copyTranscript} className="text-muted-foreground">
                <Copy />
                <span className="sr-only">{tr.toolbar.copyTranscript}</span>
              </Button>

              <DropdownMenu open={translationMenuOpen} onOpenChange={setTranslationMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant={visibleLangs.size > 0 ? "secondary" : "outline"}
                    className="gap-1.5 px-2.5 text-muted-foreground"
                  >
                    {anyTranslationInFlight ? <Loader2 className="animate-spin" /> : <Languages />}
                    {tr.toolbar.translationMenu}
                    <ChevronDown className="size-3.5 opacity-60" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  {/* Plain rows, not DropdownMenuItem — each carries two
                      independent actions (Translate/Retranslate and View),
                      and selecting one shouldn't close the menu the way a
                      normal single-action item would. */}
                  {translateTargets.map((lang) => {
                    const job = translationJobs[lang];
                    const inFlight = startingLangs.has(lang) || (!!job && IN_FLIGHT_STATUSES.includes(job.status));
                    const everTranslated = !!translations[lang] || !!transcript.translated_languages?.includes(lang);
                    const shown = visibleLangs.has(lang);
                    return (
                      <div key={lang} className="flex items-center gap-1 rounded-sm px-2 py-1.5">
                        <span className="flex-1 truncate text-sm">{languageNames[lang] ?? lang.toUpperCase()}</span>
                        <LineActionButton
                          icon={inFlight ? Loader2 : everTranslated ? RotateCcw : Languages}
                          label={
                            inFlight
                              ? tr.toolbar.translatingEllipsis
                              : hasUnsavedChanges
                                ? tr.toolbar.saveChangesFirst
                                : everTranslated
                                  ? tr.toolbar.retranslate
                                  : tr.toolbar.translate
                          }
                          spinning={inFlight}
                          disabled={inFlight || hasUnsavedChanges}
                          onClick={() => requestTranslate(lang)}
                        />
                        <LineActionButton
                          icon={shown ? Eye : EyeOff}
                          label={tr.toolbar.view}
                          active={shown}
                          disabled={!everTranslated}
                          onClick={() => handleViewClick(lang)}
                        />
                      </div>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1.5 px-2.5 text-muted-foreground">
                    {reviewInFlight ? <Loader2 className="size-5 animate-spin" /> : <Bot className="size-5" />}
                    {tr.toolbar.aiReviewMenu}
                    <ChevronDown className="size-3.5 opacity-60" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {!hasBeenReviewed ? (
                    <DropdownMenuItem disabled={reviewInFlight || hasUnsavedChanges} onClick={requestReview}>
                      {reviewInFlight ? <Loader2 className="animate-spin" /> : <Bot />}
                      {tr.toolbar.reviewWithAI}
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => setReviewVisible((visible) => !visible)}>
                        {reviewVisible ? <EyeOff /> : <Eye />}
                        {reviewVisible ? tr.toolbar.hideReview : tr.toolbar.viewReview}
                      </DropdownMenuItem>
                      <DropdownMenuItem disabled={reviewInFlight || hasUnsavedChanges} onClick={requestReview}>
                        {reviewInFlight ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                        {tr.toolbar.regenerate}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="blue" className="gap-1.5 px-2.5">
                    <Download />
                    {tr.toolbar.export}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={isCaption ? "w-48" : "w-32"}>
                  {isCaption ? (
                    <>
                      <DropdownMenuItem onClick={handleOpenVideoExport} className="gap-1.5">
                        <Film />
                        {videoFormatLabel}
                        {videoResolutionLabel ? (
                          <span className="ms-auto text-xs text-muted-foreground">{videoResolutionLabel}</span>
                        ) : null}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {EXPORT_FORMATS.map((format) => (
                    <DropdownMenuItem
                      key={format}
                      onClick={() => {
                        setExportFormat(format);
                        setExportOpen(true);
                      }}
                    >
                      {format}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null}
        </div>
      </header>

      {!completed ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
          {transcript.status === "failed" ? (
            <>
              <AlertCircle className="size-6 text-destructive" aria-hidden="true" />
              <p className="text-sm">{transcript.failure_reason || tr.states.failedGeneric}</p>
            </>
          ) : (
            <>
              <Loader2 className="size-6 animate-spin" aria-hidden="true" />
              <p className="text-sm">{isCaption ? tr.states.transcribingVideo : tr.states.transcribingAudio}</p>
            </>
          )}
        </div>
      ) : (
        <div className="caption-editor-body flex min-h-0 flex-1 flex-col md:flex-row">
          <div
            ref={scrollRef}
            onScroll={updateJump}
            className="transcript-content-scroll caption-transcript-scroll order-2 min-h-0 min-w-0 flex-1 overflow-y-auto md:order-1"
          >
            <div className="mx-auto w-full max-w-3xl px-6 py-10">
            {suggestions.length > 0 && reviewVisible && !segmentationPreviewActive ? (
              <div className="mb-6 flex items-center gap-1.5 text-xs text-muted-foreground">
                {bulkRunning ? <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden="true" /> : null}
                <span>{tr.toolbar.aiCorrectionsCount(suggestions.length)}</span>
                <span aria-hidden="true">&middot;</span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkRunning || editsLocked}
                  onClick={() => setBulkConfirm("accepted")}
                  className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  <CheckCheck className="size-3.5" />
                  {tr.toolbar.acceptAll}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkRunning || editsLocked}
                  onClick={() => setBulkConfirm("rejected")}
                  className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  <RejectAllIcon className="size-3.5" />
                  {tr.toolbar.rejectAll}
                </Button>
              </div>
            ) : null}

            {lines.map((line, lineIndex) => {
              const armedHere = armedAction?.lineIndex === lineIndex;
              const isEditTarget = armedHere && armedAction.action === "edit";
              const isSplitTarget = armedHere && armedAction.action === "split";
              // Suppressed during a live resegmentation preview: accepting a
              // suggestion patches local `lines` directly (see
              // resolveSuggestions), and reverting the preview restores a
              // pre-preview snapshot that wouldn't include that patch —
              // same reasoning as translatedEntries below, just for a
              // mutation instead of a stale index. Also suppressed while the
              // review is toggled hidden.
              const lineSuggestions =
                segmentationPreviewActive || !reviewVisible ? undefined : suggestionsByLine.get(lineIndex);
              const hasSuggestions = !!lineSuggestions && lineSuggestions.size > 0;
              const lineResolving = hasSuggestions && Array.from(lineSuggestions.values()).some((s) => resolvingIds.has(s.id));
              // Translation segmentation is independently editable, so it
              // cannot be paired to source text by array index. Place each
              // translated line under the source cue in which it begins;
              // splitting or merging translated lines then remains visible
              // without pretending both documents share line boundaries.
              const translatedEntries = segmentationPreviewActive
                ? []
                : TRANSLATION_LANGUAGES.filter((lang) => visibleLangs.has(lang)).flatMap((lang) =>
                    (translations[lang] ?? [])
                      .map((translatedLine, translationLineIndex) => ({ lang, translatedLine, translationLineIndex }))
                      .filter(({ translatedLine }) => {
                        const isLastLine = lineIndex === lines.length - 1;
                        return (
                          translatedLine.words?.length > 0 &&
                          translatedLine.start >= line.start &&
                          (translatedLine.start < line.end || (isLastLine && translatedLine.start <= line.end))
                        );
                      })
                  );

              return (
                <div
                  key={line.words[0].source_index}
                  data-line-index={lineIndex}
                  className="group grid items-start gap-1 pb-7 md:grid-cols-[5rem_minmax(0,1fr)_auto] md:gap-4"
                >
                  <button
                    type="button"
                    data-slot="button"
                    onClick={() => seekToTime(line.start)}
                    className="appearance-none border-0 bg-transparent p-0 text-start font-mono text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:rounded-sm focus-visible:ring-1 focus-visible:ring-ring md:pt-1"
                  >
                    {formatDuration(line.start)}
                  </button>

                  <div className="grid gap-1.5">
                    <p
                      lang={languageCode}
                      dir={rtl ? "rtl" : "ltr"}
                      className={cn(
                        "m-0 rounded px-1.5 py-0.5 text-[0.95rem] leading-8 text-muted-foreground",
                        hasSuggestions && "bg-[var(--error-bg)]"
                      )}
                    >
                      {line.words.map((word, wordPos) => {
                        const isEditing =
                          editingWord?.lineIndex === lineIndex && editingWord?.wordPos === wordPos;
                        const active = currentTime >= word.start && currentTime < word.end;
                        const spoken = currentTime >= word.end;
                        const display = word.word.trim();
                        const matched = needle !== "" && display.toLowerCase().includes(needle);
                        const isCurrentMatch = matched && matches[matchIndex]?.lineIndex === lineIndex && matches[matchIndex]?.wordPos === wordPos;
                        const suggestion = lineSuggestions?.get(wordPos);
                        const title = editsLocked
                          ? tr.lineActions.titleLocked
                          : isEditing
                          ? undefined
                          : isEditTarget
                            ? tr.lineActions.titleEditWord
                            : isSplitTarget
                              ? wordPos === 0
                                ? tr.lineActions.titleChooseLaterWord
                                : tr.lineActions.titleSplitBeforeWord
                              : suggestion
                                ? tr.lineActions.titleAiSuggests(suggestion.corrected_word)
                                : tr.lineActions.titleDefault;
  
                        return (
                          <span key={word.source_index}>
                            <span
                              ref={(node) => {
                                if (active) activeWordRef.current = node;
                                if (isCurrentMatch) currentMatchRef.current = node;
                                if (isEditing && node && document.activeElement !== node) {
                                  node.focus();
                                  const range = document.createRange();
                                  range.selectNodeContents(node);
                                  const selection = window.getSelection();
                                  selection?.removeAllRanges();
                                  selection?.addRange(range);
                                }
                              }}
                              contentEditable={isEditing}
                              suppressContentEditableWarning={isEditing}
                              title={title}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (isEditing) return;
                                if (editsLocked) {
                                  seekToTime(word.start);
                                  return;
                                }
                                if (isEditTarget) {
                                  setArmedAction(null);
                                  setEditingWord({ lineIndex, wordPos });
                                  return;
                                }
                                if (isSplitTarget) {
                                  if (wordPos === 0) {
                                    toast.error(tr.lineActions.chooseWordAfterFirst);
                                    return;
                                  }
                                  setArmedAction(null);
                                  setLines((prev) => splitLine(prev, lineIndex, wordPos));
                                  setDirty(true);
                                  return;
                                }
                                seekToTime(word.start);
                              }}
                              onDoubleClick={(event) => {
                                event.stopPropagation();
                                if (editsLocked) return;
                                if (armedHere) setArmedAction(null);
                                setEditingWord({ lineIndex, wordPos });
                              }}
                              onKeyDown={
                                isEditing
                                  ? (event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        event.currentTarget.blur();
                                      }
                                      if (event.key === "Escape") {
                                        event.preventDefault();
                                        cancelEditRef.current = true;
                                        event.currentTarget.blur();
                                      }
                                    }
                                  : undefined
                              }
                              onBlur={
                                isEditing
                                  ? (event) => {
                                      const cancelled = cancelEditRef.current;
                                      cancelEditRef.current = false;
                                      const text = event.currentTarget.textContent ?? "";
                                      setEditingWord(null);
                                      if (!cancelled && !editsLocked) {
                                        commitWordEdit(lineIndex, wordPos, text);
                                      } else {
                                        event.currentTarget.textContent = display;
                                      }
                                    }
                                  : undefined
                              }
                              className={cn(
                                "cursor-pointer rounded-sm",
                                (spoken || active) && "text-foreground",
                                active && !isEditing && "underline decoration-primary decoration-2 underline-offset-4",
                                matched && !isCurrentMatch && "bg-[var(--pending-bg)]",
                                isCurrentMatch && "bg-[var(--pending)] text-black",
                                suggestion && "bg-[var(--error-bg-strong)] text-foreground",
                                isEditing &&
                                  "cursor-text bg-accent px-0.5 outline-none ring-2 ring-ring/40 empty:inline-block empty:min-w-2"
                              )}
                            >
                              {display}
                            </span>{" "}
                          </span>
                        );
                      })}
                    </p>

                    {hasSuggestions ? (
                      <p
                        lang={languageCode}
                        dir={rtl ? "rtl" : "ltr"}
                        className="m-0 rounded bg-[var(--success-bg)] px-1.5 py-0.5 text-[0.95rem] leading-8 text-muted-foreground"
                      >
                        {line.words.map((word, wordPos) => {
                          const suggestion = lineSuggestions.get(wordPos);
                          return (
                            <span key={word.source_index}>
                              <span
                                className={cn(
                                  "rounded-sm",
                                  suggestion && "bg-[var(--success-bg-strong)] text-foreground"
                                )}
                              >
                                {suggestion ? suggestion.corrected_word : word.word.trim()}
                              </span>{" "}
                            </span>
                          );
                        })}
                      </p>
                    ) : null}

                    {translatedEntries.map(({ lang, translatedLine, translationLineIndex }) => {
                      const translationArmedHere =
                        translationArmedAction?.lang === lang &&
                        translationArmedAction.lineIndex === translationLineIndex;
                      const translationEditTarget =
                        translationArmedHere && translationArmedAction.action === "edit";
                      const translationSplitTarget =
                        translationArmedHere && translationArmedAction.action === "split";
                      const translationSaving = savingTranslations.has(lang);
                      const translationDirty = dirtyTranslations.has(lang);

                      return (
                        <div
                          key={`${lang}:${translatedLine.words[0].source_index}`}
                          data-translation-line={`${lang}:${translationLineIndex}`}
                          className="group/translation flex items-start gap-1"
                        >
                          <p
                            lang={lang}
                            dir={isRtlLanguage(lang) ? "rtl" : "ltr"}
                            className="m-0 min-w-0 flex-1 text-[0.95rem] leading-8 text-muted-foreground"
                          >
                            <span className="me-1.5 font-mono text-[0.7rem] font-semibold tracking-wide text-muted-foreground">
                              [{lang.toUpperCase()}]
                            </span>
                            {translatedLine.words.map((word, wordPos) => {
                              const isEditing =
                                editingTranslationWord?.lang === lang &&
                                editingTranslationWord.lineIndex === translationLineIndex &&
                                editingTranslationWord.wordPos === wordPos;
                              const active = currentTime >= word.start && currentTime < word.end;
                              const spoken = currentTime >= word.end;
                              const display = word.word.trim();
                              const title = editsLocked
                                ? tr.lineActions.titleLocked
                                : isEditing
                                ? undefined
                                : translationEditTarget
                                  ? tr.lineActions.titleEditTranslatedWord
                                  : translationSplitTarget
                                    ? wordPos === 0
                                      ? tr.lineActions.titleChooseLaterWord
                                      : tr.lineActions.titleSplitTranslatedBeforeWord
                                    : tr.lineActions.titleDefaultTranslation;

                              return (
                                <span key={word.source_index}>
                                  <span
                                    ref={(node) => {
                                      if (isEditing && node && document.activeElement !== node) {
                                        node.focus();
                                        const range = document.createRange();
                                        range.selectNodeContents(node);
                                        const selection = window.getSelection();
                                        selection?.removeAllRanges();
                                        selection?.addRange(range);
                                      }
                                    }}
                                    contentEditable={isEditing}
                                    suppressContentEditableWarning={isEditing}
                                    title={title}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (isEditing || translationSaving) return;
                                      if (editsLocked) {
                                        seekToTime(word.start);
                                        return;
                                      }
                                      if (translationEditTarget) {
                                        setTranslationArmedAction(null);
                                        setEditingTranslationWord({
                                          lang,
                                          lineIndex: translationLineIndex,
                                          wordPos,
                                        });
                                        return;
                                      }
                                      if (translationSplitTarget) {
                                        if (wordPos === 0) {
                                          toast.error(tr.lineActions.chooseWordAfterFirst);
                                          return;
                                        }
                                        handleTranslationSplit(lang, translationLineIndex, wordPos);
                                        return;
                                      }
                                      seekToTime(word.start);
                                    }}
                                    onDoubleClick={(event) => {
                                      event.stopPropagation();
                                      if (translationSaving || editsLocked) return;
                                      if (translationArmedHere) setTranslationArmedAction(null);
                                      setEditingTranslationWord({
                                        lang,
                                        lineIndex: translationLineIndex,
                                        wordPos,
                                      });
                                    }}
                                    onKeyDown={
                                      isEditing
                                        ? (event) => {
                                            if (event.key === "Enter") {
                                              event.preventDefault();
                                              event.currentTarget.blur();
                                            }
                                            if (event.key === "Escape") {
                                              event.preventDefault();
                                              translationCancelEditRef.current = true;
                                              event.currentTarget.blur();
                                            }
                                          }
                                        : undefined
                                    }
                                    onBlur={
                                      isEditing
                                        ? (event) => {
                                            const cancelled = translationCancelEditRef.current;
                                            translationCancelEditRef.current = false;
                                            const text = event.currentTarget.textContent ?? "";
                                            setEditingTranslationWord(null);
                                            if (!cancelled && !editsLocked) {
                                              commitTranslationWordEdit(
                                                lang,
                                                translationLineIndex,
                                                wordPos,
                                                text
                                              );
                                            } else {
                                              event.currentTarget.textContent = display;
                                            }
                                          }
                                        : undefined
                                    }
                                    className={cn(
                                      "cursor-pointer rounded-sm",
                                      (spoken || active) && "text-foreground",
                                      active && !isEditing && "underline decoration-primary decoration-2 underline-offset-4",
                                      isEditing &&
                                        "cursor-text bg-accent px-0.5 outline-none ring-2 ring-ring/40 empty:inline-block empty:min-w-2"
                                    )}
                                  >
                                    {display}
                                  </span>{" "}
                                </span>
                              );
                            })}
                          </p>

                          <div
                            className={cn(
                              "flex h-9 shrink-0 items-center gap-0.5 opacity-0 transition-opacity",
                              "group-hover/translation:opacity-100 group-focus-within/translation:opacity-100",
                              (translationArmedHere || translationDirty) && "opacity-100"
                            )}
                          >
                            <LineActionButton
                              icon={Pencil}
                              label={tr.lineActions.editTranslatedWord}
                              active={translationEditTarget}
                              disabled={translationSaving || editsLocked}
                              onClick={() => toggleTranslationArmed(lang, translationLineIndex, "edit")}
                            />
                            <LineActionButton
                              icon={Scissors}
                              label={tr.lineActions.splitTranslatedLine}
                              active={translationSplitTarget}
                              disabled={translationSaving || editsLocked}
                              onClick={() => toggleTranslationArmed(lang, translationLineIndex, "split")}
                            />
                            <LineActionButton
                              icon={ArrowUpToLine}
                              label={tr.lineActions.mergeTranslatedAbove}
                              disabled={translationSaving || editsLocked || translationLineIndex === 0}
                              onClick={() => handleTranslationMerge(lang, translationLineIndex, "above")}
                            />
                            <LineActionButton
                              icon={ArrowDownToLine}
                              label={tr.lineActions.mergeTranslatedBelow}
                              disabled={translationSaving || editsLocked || translationLineIndex === translations[lang].length - 1}
                              onClick={() => handleTranslationMerge(lang, translationLineIndex, "below")}
                            />
                            <LineActionButton
                              icon={translationSaving ? Loader2 : Save}
                              label={translationSaving ? tr.lineActions.savingTranslation : tr.lineActions.saveTranslationChanges}
                              spinning={translationSaving}
                              disabled={editsLocked || !translationDirty || isAnythingSaving}
                              onClick={handleSave}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    {/* h-9 matches a text line's own box (leading-8 + py-0.5
                        = 32px + 4px), so this row's icons land level with the
                        original line regardless of hover state, and the
                        accept/reject row below lands level with the
                        corrected line the same way. */}
                    <div
                      className={cn(
                        "flex h-9 items-center gap-0.5 opacity-0 transition-opacity",
                        "group-hover:opacity-100 group-focus-within:opacity-100",
                        armedHere && "opacity-100"
                      )}
                    >
                      <LineActionButton
                        icon={Pencil}
                        label={tr.lineActions.editWord}
                        active={isEditTarget}
                        disabled={editsLocked}
                        onClick={() => toggleArmed(lineIndex, "edit")}
                      />
                      <LineActionButton
                        icon={Scissors}
                        label={tr.lineActions.splitLine}
                        active={isSplitTarget}
                        disabled={editsLocked}
                        onClick={() => toggleArmed(lineIndex, "split")}
                      />
                      <LineActionButton
                        icon={ArrowUpToLine}
                        label={tr.lineActions.mergeAbove}
                        disabled={editsLocked || lineIndex === 0}
                        onClick={() => handleMerge(lineIndex, "above")}
                      />
                      <LineActionButton
                        icon={ArrowDownToLine}
                        label={tr.lineActions.mergeBelow}
                        disabled={editsLocked || lineIndex === lines.length - 1}
                        onClick={() => handleMerge(lineIndex, "below")}
                      />
                    </div>

                    {hasSuggestions ? (
                      <div className="flex h-9 items-center gap-0.5">
                        <LineActionButton
                          icon={Check}
                          label={tr.lineActions.acceptCorrection}
                          disabled={editsLocked || lineResolving || bulkRunning}
                          onClick={() => handleResolveLine(lineIndex, "accepted")}
                        />
                        <LineActionButton
                          icon={X}
                          label={tr.lineActions.rejectCorrection}
                          disabled={editsLocked || lineResolving || bulkRunning}
                          onClick={() => handleResolveLine(lineIndex, "rejected")}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          </div>

          {isCaption ? (
            <div
              ref={videoColumnRef}
              className="caption-video-column order-1 flex min-h-0 min-w-0 w-full flex-col shrink-0 items-center border-b border-border bg-black p-3 md:order-2 md:w-80 md:items-start md:border-b-0 md:border-s md:p-4 lg:w-96"
            >
              {/* w-full, unconditional (not md:-only): the video's own width
                  classes are percentages, and this wrapper is otherwise a
                  shrink-to-fit flex item along its parent's main axis at
                  every breakpoint — a percentage child can't resolve against
                  an ancestor whose own width depends on it, so without this
                  the video fell back to its raw intrinsic pixel size
                  (usually far larger than the column, clipped by the app
                  shell's overflow-hidden) and the caption, pinned to that
                  oversized box's bottom edge, ended up below the visible,
                  cropped video instead of overlaid on it. Height needs no
                  equivalent fix: the parent is flex-col, so this wrapper's
                  height is its own main-axis (content-based) size, not a
                  stretched cross-axis one — and that content-based size is
                  now capped by the measured videoMaxHeight (see the effect
                  above), which is what actually keeps the column from
                  needing to scroll: the video shrinks to fit whatever room
                  is left after the panel, rather than the panel fighting an
                  oversized video for space. */}
              <div ref={videoStageRef} className="caption-video-stage relative flex w-full min-w-0 shrink-0 justify-center overflow-hidden bg-black">
                <video
                  ref={audioRef}
                  src={mediaUrl}
                  preload="metadata"
                  playsInline
                  className="caption-preview-video block h-auto w-full max-w-full rounded-md object-contain"
                  style={{
                    // Fullscreen is requested on videoStageRef (see above),
                    // which the browser forces to fill the viewport — so
                    // 100% here resolves against that, the same max-height-
                    // clamp mechanism as the normal-mode cap below, just
                    // with a different ceiling. Rounded corners look wrong
                    // filling an actual screen, so those drop too.
                    maxHeight: isFullscreen ? "100%" : (videoMaxHeight ?? undefined),
                    borderRadius: isFullscreen ? 0 : undefined,
                  }}
                  {...mediaHandlers}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      data-slot="button"
                      aria-label={isFullscreen ? tr.player.exitFullscreen : tr.player.enterFullscreen}
                      onClick={handleToggleFullscreen}
                      className={cn(
                        "absolute end-2 z-20 flex size-7 items-center justify-center rounded-md border-0 bg-black/60 text-white/80 outline-none transition-colors hover:bg-black/80 hover:text-white focus-visible:ring-1 focus-visible:ring-ring",
                        isFullscreen ? "top-2" : "bottom-2"
                      )}
                    >
                      {isFullscreen ? <Minimize className="size-3.5" /> : <Maximize className="size-3.5" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">{isFullscreen ? tr.player.exitFullscreen : tr.player.enterFullscreen}</TooltipContent>
                </Tooltip>
                {isFullscreen ? (
                  <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/90 via-black/65 to-transparent px-4 pb-4 pt-10 text-white">
                    <div
                      role="slider"
                      tabIndex={0}
                      aria-label={tr.player.seekVideo}
                      aria-valuemin={0}
                      aria-valuemax={Math.round(duration)}
                      aria-valuenow={Math.round(currentTime)}
                      aria-valuetext={formatDuration(currentTime)}
                      onPointerDown={(event) => {
                        event.currentTarget.setPointerCapture(event.pointerId);
                        setScrubbing(true);
                        seekFromPointer(event.clientX, event.currentTarget);
                      }}
                      onPointerMove={(event) => {
                        if (scrubbing) seekFromPointer(event.clientX, event.currentTarget);
                      }}
                      onPointerUp={(event) => {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                        setScrubbing(false);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowLeft") seekBy(-5);
                        if (event.key === "ArrowRight") seekBy(5);
                      }}
                      className="group/fullscreen-seek relative flex h-5 w-full cursor-pointer touch-none items-center focus-visible:outline-none"
                    >
                      <div className="h-1 w-full overflow-hidden rounded-full bg-white/30 transition-[height] group-hover/fullscreen-seek:h-1.5 group-focus-visible/fullscreen-seek:h-1.5">
                        <div
                          className="h-full bg-white"
                          style={{ width: `${(currentTime / Math.max(1, duration)) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        data-slot="button"
                        aria-label={playing ? tr.player.pauseVideo : tr.player.playVideo}
                        onClick={togglePlay}
                        className="flex size-9 appearance-none items-center justify-center rounded-md border-0 bg-transparent p-0 text-white outline-none transition-colors hover:bg-white/15 focus-visible:ring-1 focus-visible:ring-white"
                      >
                        {playing ? <Pause className="size-5 fill-current" /> : <Play className="size-5 fill-current" />}
                      </button>
                      <span className="ms-1 font-mono text-xs tabular-nums text-white/80">
                        {formatDuration(currentTime)} / {formatDuration(duration)}
                      </span>
                      <button
                        type="button"
                        data-slot="button"
                        aria-label={tr.player.changeSpeed}
                        onClick={() => setSpeedIndex((index) => (index + 1) % SPEEDS.length)}
                        className="ms-auto appearance-none rounded-md border-0 bg-transparent px-2 py-1 font-mono text-xs text-white/80 outline-none transition-colors hover:bg-white/15 hover:text-white focus-visible:ring-1 focus-visible:ring-white"
                      >
                        {SPEEDS[speedIndex]}x
                      </button>
                    </div>
                  </div>
                ) : null}
                {captionBlocks.length > 0 ? (
                  <div
                    className={cn(
                      "pointer-events-none absolute left-1/2 flex w-[90%] flex-col items-center",
                      captionStyle.backgroundMode === "box" ? "gap-1.5" : "gap-0.5"
                    )}
                    style={{
                      // positionY is the vertical center of the complete
                      // language stack. Every language remains its own box,
                      // so background bands never fuse into one block.
                      top: `${captionStyle.positionY}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    {captionBlocks.map(({ lang, text }) => (
                      <p
                        key={lang}
                        lang={lang}
                        dir={isRtlLanguage(lang) ? "rtl" : "ltr"}
                        className="m-0 w-max max-w-full rounded"
                        style={{
                          textAlign: captionStyle.textAlign,
                          fontSize: captionStyle.size * captionPreviewScale,
                          ...captionTextStyle(captionStyle),
                          ...captionBackgroundStyle(captionStyle, captionPreviewScale),
                        }}
                      >
                        {text}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>

              <div ref={captionPanelWrapRef} className="caption-style-panel-wrap w-full min-w-0 shrink-0">
                <CaptionStylePanel
                  style={captionStyle}
                  onChange={setCaptionStyle}
                  languageOptions={captionLanguageOptions}
                  selectedLanguages={effectiveCaptionLanguages}
                  languagesEnabled={captionTranslationCodes.length > 0}
                  loadingLanguages={loadingCaptionLanguages}
                  onLanguagesChange={handleCaptionLanguagesChange}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {completed ? (
        <div className="transcript-player relative shrink-0 border-t border-border">
          {jump ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                activeWordRef.current?.scrollIntoView({ block: "center", behavior: "smooth" })
              }
              className="absolute -top-12 left-1/2 -translate-x-1/2 border border-border shadow-lg"
            >
              {jump === "up" ? <ArrowUp /> : <ArrowDown />}
              {tr.player.jumpToCurrentWord}
            </Button>
          ) : null}

          <div
            role="slider"
            tabIndex={0}
            aria-label={tr.player.seek}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(currentTime)}
            aria-valuetext={formatDuration(currentTime)}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              setScrubbing(true);
              seekFromPointer(event.clientX, event.currentTarget);
            }}
            onPointerMove={(event) => {
              if (scrubbing) seekFromPointer(event.clientX, event.currentTarget);
            }}
            onPointerUp={(event) => {
              event.currentTarget.releasePointerCapture(event.pointerId);
              setScrubbing(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") seekBy(-5);
              if (event.key === "ArrowRight") seekBy(5);
            }}
            className="group/seek relative flex h-4 w-full cursor-pointer touch-none items-center focus-visible:outline-none"
          >
            <div className="h-1 w-full bg-muted transition-[height] group-hover/seek:h-1.5 group-focus-visible/seek:h-1.5">
              <div
                className="h-full bg-primary"
                style={{ width: `${(currentTime / Math.max(1, duration)) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5">
            <Button size="icon-sm" variant="ghost" onClick={togglePlay}>
              {playing ? <Pause className="fill-current" /> : <Play className="fill-current" />}
              <span className="sr-only">{playing ? tr.player.pause : tr.player.play}</span>
            </Button>

            <Button size="icon-sm" variant="ghost" onClick={() => seekBy(-5)} className="text-muted-foreground">
              <SeekBack />
              <span className="sr-only">{tr.player.back5}</span>
            </Button>
            <Button size="icon-sm" variant="ghost" onClick={() => seekBy(5)} className="text-muted-foreground">
              <SeekForward />
              <span className="sr-only">{tr.player.forward5}</span>
            </Button>

            <span className="font-mono text-xs tabular-nums text-muted-foreground">
              {formatDuration(currentTime)}
            </span>

            <button
              type="button"
              data-slot="button"
              onClick={() => setSpeedIndex((index) => (index + 1) % SPEEDS.length)}
              className="ms-auto appearance-none rounded-md border-0 bg-transparent px-2 py-1 font-mono text-xs text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
            >
              {SPEEDS[speedIndex]}x
            </button>
          </div>

          {isCaption ? null : (
            <audio
              ref={audioRef}
              src={mediaUrl}
              preload="metadata"
              className="hidden"
              {...mediaHandlers}
            />
          )}
        </div>
      ) : null}

      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title={tr.dialogs.renameTitle}
        label={tr.dialogs.nameLabel}
        initialValue={transcriptName(transcript, language)}
        submitLabel={tr.dialogs.save}
        cancelLabel={tr.dialogs.cancel}
        onSubmit={handleRename}
      />

      {completed ? (
        <ExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          fileName={transcriptName(transcript, language)}
          languageCode={languageCode}
          lines={lines}
          translations={translations}
          initialFormat={exportFormat}
        />
      ) : null}

      {isCaption ? (
        <VideoExportDialog
          open={videoExportDialogOpen}
          onOpenChange={setVideoExportDialogOpen}
          job={videoExportJob}
          downloading={downloadingVideo}
          downloadProgress={videoDownloadProgress}
          onDownload={handleDownloadVideo}
          onRetry={handleExportVideo}
        />
      ) : null}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tr.dialogs.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tr.dialogs.deleteDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr.dialogs.cancel}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {tr.dialogs.deleteConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!creditConfirmation} onOpenChange={(open) => !open && setCreditConfirmation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {creditConfirmation?.type === "translation"
                ? tr.dialogs.translationCreditTitle(creditCost)
                : tr.dialogs.reviewCreditTitle(creditCost)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {creditConfirmation?.type === "translation"
                ? tr.dialogs.translationCreditDescription(creditCost)
                : tr.dialogs.reviewCreditDescription(creditCost)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr.dialogs.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreditConfirm}>{tr.dialogs.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resizeConfirmOpen} onOpenChange={setResizeConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tr.dialogs.resizeTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tr.dialogs.resizeDescription(wordsPerLine)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr.dialogs.cancel}</AlertDialogCancel>
            <AlertDialogAction disabled={editsLocked} onClick={handleConfirmResize}>{tr.dialogs.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!bulkConfirm} onOpenChange={(open) => !open && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkConfirm === "accepted" ? tr.dialogs.acceptAllTitle : tr.dialogs.rejectAllTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkConfirm === "accepted"
                ? tr.dialogs.acceptAllDescription(suggestions.length)
                : tr.dialogs.rejectAllDescription(suggestions.length)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr.dialogs.cancel}</AlertDialogCancel>
            <AlertDialogAction disabled={editsLocked} onClick={() => handleResolveAll(bulkConfirm)}>{tr.dialogs.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
